import React from 'react';
import { RefreshCw, TrendingUp, Calendar, AlertOctagon, Globe, MousePointerClick } from 'lucide-react';
import { StatCard } from './adminShared';

// Admin dashboard "traffic" tab (extracted from AdminPage).
export const AdminTrafficTab = ({
  fetchVisitorStats,
  hideMyVisits,
  scrubBotVisits,
  setHideMyVisits,
  setVisitorRange,
  users,
  visitorRange,
  visitorStats,
}) => (
  <div className="space-y-6">
    {/* Range scale toggle: changes the daily-breakdown chart below */}
    <div className="flex items-center gap-2 flex-wrap justify-between" data-testid="traffic-range-toggle">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-zinc-500 mr-1">Chart range:</span>
        {[
          { v: 1, label: 'Today' },
          { v: 7, label: '7 days' },
          { v: 30, label: '30 days' },
          { v: 90, label: '90 days' },
          { v: 365, label: '1 year' },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setVisitorRange(opt.v)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              visitorRange === opt.v
                ? 'bg-white text-black border-white'
                : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-zinc-500'
            }`}
            data-testid={`traffic-range-${opt.v}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {/* Hide-my-visits TOGGLE (non-destructive filter — replaced the
          old "Remove my visits" button that PERMANENTLY DELETED rows
          and caused the all-zero analytics bug). */}
      <button
        onClick={() => setHideMyVisits((v) => !v)}
        className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
          hideMyVisits
            ? 'bg-fuchsia-500/20 border-fuchsia-500/50 text-fuchsia-200'
            : 'bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800 hover:text-white'
        }`}
        data-testid="toggle-hide-my-visits"
        title="Filter out any IP that has ever logged in (yours, your creators'). Doesn't delete anything."
      >
        <span className={`w-1.5 h-1.5 rounded-full ${hideMyVisits ? 'bg-fuchsia-400' : 'bg-zinc-500'}`} />
        {hideMyVisits ? 'Hiding signed-in IPs' : 'Hide signed-in IPs'}
      </button>
      {/* DESTRUCTIVE bot-row purge — kept but visually marked as red. */}
      <button
        onClick={scrubBotVisits}
        className="text-xs px-3 py-1.5 rounded-full border border-red-700/60 bg-red-900/20 text-red-300 hover:bg-red-900/40 hover:text-red-200 transition-colors flex items-center gap-1.5"
        data-testid="scrub-bot-visits-btn"
        title="DESTRUCTIVE: permanently deletes historical visits from ChatGPT, Claude, Perplexity, social crawlers, etc."
      >
        <AlertOctagon className="w-3.5 h-3.5" />
        Purge bot rows
      </button>
    </div>

    {/* Unique visitors — across scales */}
    <div>
      <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Unique visitors</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Globe} label="Today" value={visitorStats.summary?.unique_today || 0} color="cyan" />
        <StatCard icon={TrendingUp} label="This Week" value={visitorStats.summary?.unique_7d || 0} color="orange" />
        <StatCard icon={Calendar} label="This Month" value={visitorStats.summary?.unique_30d || 0} color="purple" />
        <StatCard icon={Globe} label="All Time" value={visitorStats.summary?.unique_all || 0} color="green" />
      </div>
    </div>

    {/* Page views — across scales */}
    <div>
      <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Page views (clicks)</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={MousePointerClick} label="Today" value={visitorStats.summary?.visits_today || 0} color="cyan" />
        <StatCard icon={MousePointerClick} label="This Week" value={visitorStats.summary?.visits_7d || 0} color="orange" />
        <StatCard icon={MousePointerClick} label="This Month" value={visitorStats.summary?.visits_30d || 0} color="purple" />
        <StatCard icon={MousePointerClick} label="All Time" value={visitorStats.summary?.visits_all || 0} color="green" />
      </div>
    </div>

    {/* Daily breakdown bar chart */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden" data-testid="traffic-daily-card">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-white">Daily Traffic</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Unique visitors and page views per day · {visitorRange === 1 ? 'Today' : `Last ${visitorRange} days`}
          </p>
        </div>
        <button
          onClick={fetchVisitorStats}
          className="text-xs px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center gap-1.5"
          data-testid="traffic-refresh-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>
      <div className="p-5">
        {visitorStats.daily.length === 0 ? (
          <div className="text-center text-zinc-500 text-sm py-10">
            No visits recorded yet. Visit data will appear here as users browse the app.
          </div>
        ) : (
          <div className="space-y-2">
            {(() => {
              const maxVisits = Math.max(1, ...visitorStats.daily.map(d => d.visits || 0));
              return visitorStats.daily.slice(0, visitorRange).map((day) => {
                const pct = ((day.visits || 0) / maxVisits) * 100;
                const dateStr = new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                return (
                  <div key={day.date} className="flex items-center gap-3">
                    <span className="text-xs text-zinc-400 w-24 flex-shrink-0">{dateStr}</span>
                    <div className="flex-1 h-6 bg-zinc-800/50 rounded-md overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-purple-500 flex items-center px-2"
                        style={{ width: `${pct}%`, minWidth: pct > 0 ? '6px' : 0 }}
                      />
                    </div>
                    <div className="flex items-center gap-3 w-32 justify-end">
                      <span className="text-xs text-orange-400 font-semibold">{day.uniques}</span>
                      <span className="text-xs text-zinc-500">{day.visits} views</span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    </div>

    {/* Top pages + referrers */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden" data-testid="traffic-top-pages-card">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h3 className="font-semibold text-white">Top Pages</h3>
        </div>
        <div className="divide-y divide-zinc-800">
          {visitorStats.top_pages.length === 0 ? (
            <div className="px-5 py-6 text-center text-zinc-500 text-sm">No data yet</div>
          ) : visitorStats.top_pages.map((p) => (
            <div key={p.path} className="px-5 py-3 flex items-center justify-between">
              <span className="text-sm text-zinc-200 font-mono truncate max-w-[60%]">{p.path}</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-orange-400 font-semibold">{p.uniques} uniques</span>
                <span className="text-xs text-zinc-500">{p.visits} views</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden" data-testid="traffic-referrers-card">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h3 className="font-semibold text-white">Top Referrers</h3>
        </div>
        <div className="divide-y divide-zinc-800">
          {visitorStats.top_referrers.length === 0 ? (
            <div className="px-5 py-6 text-center text-zinc-500 text-sm">No referrers tracked yet</div>
          ) : visitorStats.top_referrers.map((r, idx) => (
            <div key={r.referrer || `ref-${idx}`} className="px-5 py-3 flex items-center justify-between">
              <span className="text-sm text-zinc-200 truncate max-w-[70%]" title={r.referrer}>{r.referrer}</span>
              <span className="text-xs text-zinc-500">{r.visits}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);
